import React from 'react';
import { DateRangePicker, DateRangePickerProps } from '@amzn/awsui-components-react';
import { TextFunction, useLocalizedText } from '../hooks/localizedText';

export interface AbsoluteDatePickerProps {
  onChange: (value: DateRangePickerProps.Value | null) => void;
  value: DateRangePickerProps.Value | null;
}

// Visible for testing
export function isValidRange(
  t: TextFunction,
  range: DateRangePickerProps.Value | null
): DateRangePickerProps.ValidationResult {
  if (range && range.type === 'absolute') {
    const [startDateWithoutTime] = range.startDate.split('T');
    const [endDateWithoutTime] = range.endDate.split('T');
    if (!startDateWithoutTime || !endDateWithoutTime) {
      return {
        valid: false,
        errorMessage: t('absoluteDatePicker.incomplete'),
      };
    }

    if (new Date(range.startDate).getTime() > new Date(range.endDate).getTime()) {
      return {
        valid: false,
        errorMessage: t('absoluteDatePicker.invalid'),
      };
    }
  }
  return { valid: true };
}

/**
 * The picker's labels, read one at a time. Translated bundles are flat id→string, so the whole
 * I18nStrings object cannot be read in one go the way an i18next `returnObjects` key could.
 */
function datePickerStrings(t: TextFunction): DateRangePickerProps.I18nStrings {
  return {
    absoluteModeTitle: t('absoluteDatePicker.absoluteModeTitle'),
    relativeModeTitle: t('absoluteDatePicker.relativeModeTitle'),
    relativeRangeSelectionHeading: t('absoluteDatePicker.relativeRangeSelectionHeading'),
    applyButtonLabel: t('absoluteDatePicker.applyButtonLabel'),
    cancelButtonLabel: t('absoluteDatePicker.cancelButtonLabel'),
    clearButtonLabel: t('absoluteDatePicker.clearButtonLabel'),
    customRelativeRangeOptionLabel: t('absoluteDatePicker.customRelativeRangeOptionLabel'),
    customRelativeRangeOptionDescription: t(
      'absoluteDatePicker.customRelativeRangeOptionDescription'
    ),
    customRelativeRangeDurationLabel: t('absoluteDatePicker.customRelativeRangeDurationLabel'),
    customRelativeRangeDurationPlaceholder: t(
      'absoluteDatePicker.customRelativeRangeDurationPlaceholder'
    ),
    customRelativeRangeUnitLabel: t('absoluteDatePicker.customRelativeRangeUnitLabel'),
    startDateLabel: t('absoluteDatePicker.startDateLabel'),
    startTimeLabel: t('absoluteDatePicker.startTimeLabel'),
    endDateLabel: t('absoluteDatePicker.endDateLabel'),
    endTimeLabel: t('absoluteDatePicker.endTimeLabel'),
    dateTimeConstraintText: t('absoluteDatePicker.dateTimeConstraintText'),
    todayAriaLabel: t('absoluteDatePicker.todayAriaLabel'),
    nextMonthAriaLabel: t('absoluteDatePicker.nextMonthAriaLabel'),
    previousMonthAriaLabel: t('absoluteDatePicker.previousMonthAriaLabel'),
    formatRelativeRange: ({ amount, unit }) =>
      t('absoluteDatePicker.formatRelativeRange', { amount, unit }),
    formatUnit: (unit, amount) => t('absoluteDatePicker.formatUnit', { amount, unit }),
  };
}

export function AbsoluteDatePicker(props: AbsoluteDatePickerProps) {
  const { t } = useLocalizedText(['translation']);
  const i18nStrings = datePickerStrings(t);

  return (
    <DateRangePicker
      onChange={({ detail }) => props.onChange(detail.value)}
      value={props.value}
      relativeOptions={[]}
      isValidRange={(range) => isValidRange(t, range)}
      i18nStrings={i18nStrings}
      placeholder={t('absoluteDatePicker.placeholder')}
      dateOnly
      rangeSelectorMode="absolute-only"
      expandToViewport
    />
  );
}
